/**
 * HomeAndLandScreen — one question at a time.
 *
 * Sub-cards:
 *  0  herbKnowledge          yes/no
 *  1  plantsOwnFruitVeg      yes/no
 *  2  interestedInFruitForest yes/no
 *  3  preppingForCalamities  yes/no
 *  4  communityFarming       yes/no
 *  5  canningAndPreserving   yes/no
 *  6  survivalSkills         yes/no
 *  7  specialSkills          free text (skippable — not a gate)
 */
import { useState } from "react";
import { Button } from "../components/Button";
import { QuestionCard } from "../components/QuestionCard";
import { useFlow } from "../flow/FlowContext";
import { useT } from "../i18n";

const TOTAL = 8;

export function HomeAndLandScreen() {
  const { profileAnswers, setProfileAnswers, next, canContinue } = useFlow();
  const t = useT();
  const h = profileAnswers.home;

  const patch = (partial: Partial<typeof h>) =>
    setProfileAnswers((p) => ({ ...p, home: { ...p.home, ...partial } }));

  const [card, setCard] = useState(0);
  const pos = t("questionnaire.progressLabel", { current: String(card + 1), total: String(TOTAL) });
  const goNext = () => setCard((i) => Math.min(i + 1, TOTAL - 1));
  const goBack = () => setCard((i) => Math.max(i - 1, 0));

  type HKey = "herbKnowledge" | "plantsOwnFruitVeg" | "interestedInFruitForest" | "preppingForCalamities" | "communityFarming" | "canningAndPreserving" | "survivalSkills";

  const yesNoCards: { question: string; key: HKey }[] = [
    { question: t("home.herbKnowledge"), key: "herbKnowledge" },
    { question: t("home.plantOwn"), key: "plantsOwnFruitVeg" },
    { question: t("home.fruitForest"), key: "interestedInFruitForest" },
    { question: t("home.prepping"), key: "preppingForCalamities" },
    { question: t("home.communityFarming"), key: "communityFarming" },
    { question: t("home.canning"), key: "canningAndPreserving" },
    { question: t("home.survival"), key: "survivalSkills" },
  ];

  if (card < 7) {
    const current = yesNoCards[card];
    const currentValue = h[current.key] as boolean | null;
    return (
      <div className="screen-enter" style={{ flex: 1 }}>
        <QuestionCard question={current.question} position={pos}>
          <div className="q-options">
            <button
              type="button"
              className={`q-option${currentValue === true ? " is-selected" : ""}`}
              onClick={() => { patch({ [current.key]: true }); goNext(); }}
              aria-pressed={currentValue === true}
            >
              <strong>{t("common.yes")}</strong>
            </button>
            <button
              type="button"
              className={`q-option${currentValue === false ? " is-selected" : ""}`}
              onClick={() => { patch({ [current.key]: false }); goNext(); }}
              aria-pressed={currentValue === false}
            >
              <strong>{t("common.no")}</strong>
            </button>
          </div>
          {card > 0 && (
            <button type="button" className="q-subnav__back" style={{ marginTop: 16 }} onClick={goBack}>
              ← {t("shell.back")}
            </button>
          )}
        </QuestionCard>
      </div>
    );
  }

  /* ── Sub-card 7 (last): Special skills — skippable free text ── */
  return (
    <div className="screen-enter" style={{ flex: 1 }}>
      <QuestionCard
        question={t("home.specialSkills")}
        subtext={t("home.specialPlaceholder")}
        position={pos}
        skippable
        onSkip={next}
      >
        <label className="q-free">
          <textarea
            className="q-free__textarea"
            rows={4}
            maxLength={400}
            placeholder={t("home.specialPlaceholder")}
            value={h.specialSkills}
            onChange={(e) => patch({ specialSkills: e.target.value })}
          />
          <p className="q-free__soft">{t("questionnaire.fewSentences")}</p>
        </label>
        <button type="button" className="q-subnav__back" style={{ marginTop: 12 }} onClick={goBack}>
          ← {t("shell.back")}
        </button>
      </QuestionCard>
      <div className="shell__footer" style={{ paddingLeft: 0, paddingRight: 0 }}>
        <Button onClick={next} disabled={!canContinue}>
          {t("common.continue")}
        </Button>
      </div>
    </div>
  );
}
